/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════
// FormalistePicker — popover de sélection d'un formaliste avec 2 sections :
//   - Lexback (équipe interne, badge or)
//   - Client (formalistes du workspace du dossier)
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';
const api = axios.create({ baseURL: API });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('lexback_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const DARK = {
  bg: '#1C1C28', bgSec: '#252533', border: '#3A3A4A',
  text: '#EDEAE4', textSec: '#B8B8C4', textTer: '#7E7E8E', or: '#D4A843',
};

// Cache simple par workspace_id
const _cache = {};

export default function FormalistePicker({
  currentValue,            // formaliste_id ou null
  currentName,             // nom affiché si lecture seule (fallback formaliste_airtable)
  workspaceId,             // workspace du dossier
  onChange,                // (formalisteId | null) => void
  disabled = false,
  theme = 'light',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ lexback: [], client: [] });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const isDark = theme === 'dark';

  const C = isDark ? {
    bg: DARK.bg, bgSec: DARK.bgSec, border: DARK.border,
    text: DARK.text, textSec: DARK.textSec, textTer: DARK.textTer,
    accent: DARK.or, accentBg: 'rgba(212,168,67,0.10)',
    hoverBg: DARK.bgSec, lexbackBg: 'rgba(212,168,67,0.12)', lexbackText: DARK.or,
    clientBg: 'rgba(91,141,239,0.12)', clientText: '#5B8DEF',
  } : {
    bg: 'var(--color-background-primary)',
    bgSec: 'var(--color-background-secondary)',
    border: 'var(--color-border-primary)',
    text: 'var(--color-text-primary)',
    textSec: 'var(--color-text-secondary)',
    textTer: 'var(--color-text-tertiary)',
    accent: '#B8860B', accentBg: 'rgba(184,134,11,0.06)',
    hoverBg: 'var(--color-background-secondary)',
    lexbackBg: 'rgba(184,134,11,0.10)', lexbackText: '#B8860B',
    clientBg: 'rgba(59,130,246,0.10)', clientText: '#3b82f6',
  };

  // Charger formalistes au open
  useEffect(() => {
    if (!open) return;
    const cacheKey = workspaceId || 'global';
    if (_cache[cacheKey]) {
      setData(_cache[cacheKey]);
      return;
    }
    setLoading(true);
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    api.get('/api/admin/formalistes-pickable', { params })
      .then(r => {
        _cache[cacheKey] = r.data;
        setData(r.data);
      })
      .catch(() => setData({ lexback: [], client: [] }))
      .finally(() => setLoading(false));
  }, [open, workspaceId]);

  // Click outside
  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  // Trouver le formaliste actuellement sélectionné (parmi les 2 groupes)
  const all = [...(data.lexback || []), ...(data.client || [])];
  const selected = all.find(u => u.id === currentValue);
  const displayName = selected?.name || currentName;
  const isLexback = selected ? (data.lexback || []).some(u => u.id === selected.id) : false;

  // Filtrer par recherche
  const matchSearch = (u) => !search || (u.name || '').toLowerCase().includes(search.toLowerCase());
  const filteredLexback = (data.lexback || []).filter(matchSearch);
  const filteredClient = (data.client || []).filter(matchSearch);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onClick={() => !disabled && setOpen(!open)}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          padding: '4px 8px',
          borderRadius: '6px',
          border: open ? `1px solid ${C.accent}` : '1px solid transparent',
          background: open ? C.accentBg : 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: '90px',
          fontSize: '12px',
        }}
        onMouseEnter={e => { if (!disabled && !open) e.currentTarget.style.background = C.hoverBg; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        {displayName ? (
          <>
            {selected && (
              <span style={{
                fontSize: '8px', fontWeight: 600,
                padding: '1px 5px', borderRadius: '3px',
                background: isLexback ? C.lexbackBg : C.clientBg,
                color: isLexback ? C.lexbackText : C.clientText,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{isLexback ? 'L' : 'C'}</span>
            )}
            <span style={{ color: C.text }}>{displayName}</span>
          </>
        ) : (
          <span style={{ color: C.textTer, fontStyle: 'italic' }}>+ Assigner</span>
        )}
      </div>

      {open && !disabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          minWidth: '260px',
          maxWidth: '320px',
          background: C.bg,
          border: `0.5px solid ${C.border}`,
          borderRadius: '8px',
          boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.4)' : '0 4px 14px rgba(0,0,0,0.10)',
          zIndex: 100,
          padding: '6px',
        }}>
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..."
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '6px 10px', fontSize: '12px',
              border: `0.5px solid ${C.border}`,
              borderRadius: '5px', outline: 'none',
              marginBottom: '4px',
              background: isDark ? C.bgSec : C.bg,
              color: C.text,
            }}
          />
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: C.textTer }}>
                Chargement...
              </div>
            ) : (
              <>
                {/* Section Lexback */}
                {filteredLexback.length > 0 && (
                  <div>
                    <div style={{
                      padding: '6px 8px', fontSize: '9px', textTransform: 'uppercase',
                      letterSpacing: '0.06em', color: C.lexbackText, fontWeight: 600,
                    }}>
                      Lexback
                    </div>
                    {filteredLexback.map(u => (
                      <FormalisteOption
                        key={u.id}
                        user={u}
                        isSelected={currentValue === u.id}
                        groupBg={C.lexbackBg}
                        groupText={C.lexbackText}
                        groupBadge="L"
                        onClick={() => handleSelect(u.id)}
                        C={C}
                      />
                    ))}
                  </div>
                )}

                {/* Section Client */}
                {filteredClient.length > 0 && (
                  <div style={{ marginTop: filteredLexback.length > 0 ? '6px' : 0 }}>
                    <div style={{
                      padding: '6px 8px', fontSize: '9px', textTransform: 'uppercase',
                      letterSpacing: '0.06em', color: C.clientText, fontWeight: 600,
                    }}>
                      Client
                    </div>
                    {filteredClient.map(u => (
                      <FormalisteOption
                        key={u.id}
                        user={u}
                        isSelected={currentValue === u.id}
                        groupBg={C.clientBg}
                        groupText={C.clientText}
                        groupBadge="C"
                        onClick={() => handleSelect(u.id)}
                        C={C}
                      />
                    ))}
                  </div>
                )}

                {/* Empty state */}
                {filteredLexback.length === 0 && filteredClient.length === 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: C.textTer }}>
                    {search ? 'Aucun formaliste trouvé' : 'Aucun formaliste disponible'}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Clear */}
          {currentValue && (
            <div style={{ borderTop: `0.5px solid ${C.border}`, marginTop: '4px', paddingTop: '4px' }}>
              <div onClick={handleClear} style={{
                padding: '5px 8px', cursor: 'pointer', fontSize: '11px',
                color: C.textTer, borderRadius: '4px',
              }}
              onMouseEnter={e => e.currentTarget.style.background = C.hoverBg}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Désassigner
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormalisteOption({ user, isSelected, groupBg, groupText, groupBadge, onClick, C }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '6px 8px', borderRadius: '5px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '8px',
        background: isSelected ? C.accentBg : 'transparent',
      }}
      onMouseEnter={e => e.currentTarget.style.background = isSelected ? C.accentBg : C.hoverBg}
      onMouseLeave={e => e.currentTarget.style.background = isSelected ? C.accentBg : 'transparent'}
    >
      <span style={{
        fontSize: '8px', fontWeight: 600,
        padding: '1px 5px', borderRadius: '3px',
        background: groupBg, color: groupText,
      }}>{groupBadge}</span>
      <span style={{ flex: 1, fontSize: '12px', color: C.text }}>{user.name}</span>
    </div>
  );
}

// Helper pour invalider le cache (utile après création/suppression d'un user)
export function invalidateFormalisteCache(workspaceId) {
  if (workspaceId) delete _cache[workspaceId];
  else Object.keys(_cache).forEach(k => delete _cache[k]);
}
